import DriveSub from '@/store/models/drive-subscription';
import DrivePlan from '@/store/models/drive-plan';
import byteFilter from '@/filters/byte';

const TRIAL_LENGTH = 30;

function parseDate(value) {
	if (value == null) {
		return null;
	}
	// avoid invalid date objects
	var date = new Date(value);
	return isFinite(date) ? date : null;
}

function getDisplayCode(prepaidCouponId) {
	if (!prepaidCouponId) {
		return null;
	}
	// create a display code for viewing
	return prepaidCouponId.split('_')[1];
}

function isFreePlan(plan) {
	return plan.id.includes('free');
}

function getType(sub) {
	return (sub.type === 'Prepaid' || sub.type === 'Trial' || sub.type === 'Promotion') ? sub.type : isFreePlan(sub.plan) ? 'Free' : 'Credit Card';
}

function getDisplayInterval(interval) {
	return (interval === 'monthly') ? '/ month' : '/ year';
}

function reducePlans(plans) {
	return plans.reduce(function planReducer(reducedPlans, current, index, array) {
		let planName = current.name; //enterprise
		let existingPlan = reducedPlans.find(plan => plan.name === planName);

		if (existingPlan) {
			// push another billing option into plan group
			existingPlan.billingIntervals.push(current);
		} else {
			let currentTrial;
			let billingIntervals = [];

			// if (current.hasTrial) {
			// 	//create a copy of plan
			// 	currentTrial = Object.assign({}, current);
			// 	currentTrial.amount = 0;
			// 	// change id to include trial
			// 	currentTrial.id = current.id.replace('monthly', 'trial');
			// 	//remove trial flag on non-trial
			// 	current.hasTrial = false;
			// 	billingIntervals.push(currentTrial);
			// }

			billingIntervals.push(current);

			reducedPlans.push({
				name: planName,
				contactForPricing: billingIntervals.find(interval => interval.contactForPricing) !== undefined,
				description: current.description,
				details: current.details,
				billingIntervals: billingIntervals,
				active: current.active
			});
		}
		return reducedPlans;
	},[]);
}

function formatSharing(type) {
	if (type == null) {
		return null;
	}
	return {
		value: type,
		displayValue: type === 'UNLIMITED' ? 'Unlimited Contributors' : 'Contribute to business or enterprise accounts'
	};
};

function formatAccessKeys(keyLimit) {
	if (keyLimit == null) {
		return null;
	}
	return {
		value: keyLimit,
		displayValue: keyLimit + ' Device Access Keys'
	};
}

function formatStorage(storage, customStorage) {
	if (storage == null) {
		return null;
	}
	return {
		value: storage,
		displayValue: byteFilter(storage) + (customStorage ? '+' : '') + ' of storage'
	};
}

function formatDetails(details, customStorage) {
	if (!details) {
		return;
	}
	const formattedDetails = {};
	if (details.accessKeyLimit) {
		formattedDetails.accessKeyLimit = typeof details.accessKeyLimit === 'number' ? formatAccessKeys(details.accessKeyLimit) : details.accessKeyLimit;
	}
	if (details.sharingType) {
		formattedDetails.sharingType = typeof details.sharingType === 'string' ? formatSharing(details.sharingType) : details.sharingType;
	}
	if (details.storageSizeLimit) {
		formattedDetails.storageSizeLimit = typeof details.storageSizeLimit === 'number' ? formatStorage(details.storageSizeLimit, customStorage) : details.storageSizeLimit;
	}

	return formattedDetails;
}

function createPlansHash(array) {
	return array.reduce((map, obj) => (map[obj.intervalId] = obj, map), {});
}

function deepCopy(obj) {
	return JSON.parse(JSON.stringify(obj));
}

function preparePlans(planData) {
	let plansCopy = deepCopy(planData);
	const trialAvailable = !!plansCopy.find(plan => plan.hasTrial);
	// if trial is available in state skip this step --> TODO: create a process plans action
	if (trialAvailable) {
		let plansWithTrials = deepCopy(plansCopy).filter(plan => plan.hasTrial);

		// change plan ids to make unique from monthly plan
		plansWithTrials.map(plan => {
			plan.id = plan.id.replace(plan.interval, 'trial');
			plan.amount = 0;
			return plan;
		});

		plansCopy.map(plan => {
			plan.hasTrial = false;
			return plan;
		});

		plansWithTrials.forEach((item, i) => {
			const index = plansCopy.map(plan => plan.name).indexOf(item.name);
			const trialPlan = plansCopy.find(plan => plan.id === item.id);
			if (trialPlan === undefined) {
				plansCopy.splice(index, 0, item);
			}
		});
	}

	return plansCopy.map(drivePlanFromDto);
}

function drivePlanFromDto(dto) {
	if (dto == null) {
		return null;
	}

	var plan = new DrivePlan();

	plan.id = dto.id;
	plan.name = dto.name.replace("TekDrive ","");
	plan.interval = dto.interval;
	plan.intervalId = dto.id + '_' + dto.interval;
	plan.displayInterval = getDisplayInterval(dto.interval);
	// set plans to active by default
	if (dto.active === false) {
		plan.active = dto.active;
	}
	if (dto.details) {
		plan.details = formatDetails(dto.details, dto.contactForPricing);
	}
	if (dto.amount) {
		plan.amount = dto.amount;
	}
	if (dto.paymentMethodRequired) {
		plan.paymentMethodRequired = dto.paymentMethodRequired;
	}
	if (dto.customBilling) {
		plan.customBilling = dto.customBilling;
	}
	if (dto.hasTrial) {
		plan.hasTrial = dto.hasTrial;
	}
	if (dto.contactForPricing) {
		plan.contactForPricing = dto.contactForPricing || dto.name === 'Ultimate';
	}

	return plan;
}

function getRenewalState(sub) {
	return (sub.isPromo && sub.transitionAtEnd) || (sub.isBasic && !sub.cancelAtPeriodEnd);
}

function driveSubFromDto(dto, allPlans) {
	if (dto == null) {
		return null;
	}

	let sub = new DriveSub();
	let plan = dto.plan;

	const isPortalPromo = plan.id.includes('portal');
	// TODO: get this data from API
	plan.oneTimeOffer = isPortalPromo; // need to set this one time offer key to set apart from other promos/plans
	dto.noExpiry = isPortalPromo;

	// this is a promotion with a delayed billing date, force to yearly interval
	if (plan.interval === 'every6months') {
		plan.interval = 'yearly';
	}
	// this is a promotion id that doesn't map to APIs plan list
	if (plan.id.includes('_promotional') && !plan.oneTimeOffer) {
		plan.id = plan.id.replace(/(_promotional)/i, '');
	}

	if (allPlans && allPlans.length) {
		// get current plan and pass along active flag
		const currentPlan = allPlans.find(pln => pln.id === plan.id || pln.id === plan.id + '_' + plan.interval);
		plan.active = currentPlan.active;
	}

	sub.plan = drivePlanFromDto(plan);

	sub.id = dto.id || '';
	sub.name = dto.name || 'TekDrive';
	sub.type = getType(dto); // already defined in model
	sub.createdAt = parseDate(dto.created);
	sub.updatedAt = parseDate(dto.updated);
	sub.isPrepaid = dto.type === 'Prepaid';
	sub.isTrial = dto.type === 'Trial';
	sub.isPromo = dto.type === 'Promotion';
	sub.isBasic = dto.type === 'Basic';
	sub.isFreePromo = dto.type === 'Promotion' && dto.noExpiry;
	sub.prepaidCouponId = dto.prepaidCouponId;
	sub.prepaidDisplayCode = getDisplayCode(dto.prepaidCouponId);
	// tekdrive portal promo doesn't expire so s
	sub.isOneTimeOffer = plan.oneTimeOffer;
	sub.expiry = dto.noExpiry ? null : dto.currentPeriodEnd || dto.expiry;
	sub.trialExpiry = dto.trialExpiresOn || dto.trialExpiry;
	sub.expires = !plan.active;
	sub.transitionAtEnd = dto.transitionAtEnd;
	sub.cancelAtPeriodEnd = dto.cancelAtPeriodEnd;
	sub.renews = getRenewalState(sub);
	return sub;
}

export {
	driveSubFromDto,
	drivePlanFromDto,
	reducePlans,
	createPlansHash,
	preparePlans
};



// WEBPACK FOOTER //
// ./src/modules/drive-dto.js